/**
 * Shared class strings so controls read as one system across pages.
 * Shapes are pills; panels are large-radius cards; depth comes from soft
 * shadows rather than borders.
 */

const shape =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]'

export const btn = {
  /** Default size. */
  base: `${shape} px-4 py-2 text-sm`,
  /** Compact size for toolbars and table rows. */
  sm: `${shape} px-3 py-1.5 text-xs`,
  primary:
    'bg-[var(--ink)] text-on-ink shadow-[var(--shadow-btn)] hover:-translate-y-px hover:bg-[var(--ink-hover)] active:translate-y-0',
  secondary:
    'border border-[var(--border)] bg-surface/80 text-foreground shadow-[var(--shadow-card)] backdrop-blur hover:border-[var(--border-strong)] hover:bg-surface',
  ghost: 'text-muted hover:bg-hover hover:text-foreground',
  danger: 'border border-danger-line bg-danger-soft text-danger hover:border-danger hover:bg-danger-soft/70',
}

/** Round icon-only button. Pair with an aria-label. */
export const iconBtn =
  'inline-flex h-8 w-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]'

/** Star toggle beside a library row: `cx(starBtn.base, on ? starBtn.on : starBtn.off)`.
 *  Its own primitive: appending an amber colour to `iconBtn` would lose to
 *  the utility order. */
export const starBtn = {
  base: 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
  on: 'text-warning hover:bg-warning-soft',
  off: 'text-muted/40 hover:bg-hover hover:text-warning',
}

/** Input styling without a width, for selects that size to their content. */
export const inputBase =
  'rounded-xl border border-[var(--border)] bg-surface/75 px-3.5 py-2 text-sm text-foreground transition-colors placeholder:text-muted/60 focus:border-[var(--border-strong)] focus:bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--ring)] disabled:bg-[var(--surface-raised)] disabled:text-muted'

export const input = `w-full ${inputBase}`

/** Compact input/select for narrow panels such as the 256 px canvas palette. */
export const inputSm =
  'rounded-lg border border-[var(--border)] bg-surface/75 px-2.5 py-1.5 text-xs text-foreground transition-colors focus:border-[var(--border-strong)] focus:bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--ring)]'

export const label = 'block text-[11px] font-medium uppercase tracking-[0.08em] text-muted'

/** Solid card. */
export const card = 'panel rounded-[var(--radius-card)]'

/** Frosted card for things that float over the ground. */
export const glass = 'glass rounded-[var(--radius-card)]'

/** Segmented filter pills: `cx(segment.base, on ? segment.on : segment.off)`. */
export const segment = {
  base: 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
  on: 'bg-[var(--ink)] text-on-ink shadow-sm',
  off: 'text-muted hover:bg-hover hover:text-foreground',
}

export const notice: Record<'error' | 'success' | 'info' | 'warning', string> = {
  error: 'rounded-xl border border-danger-line bg-danger-soft px-3.5 py-2.5 text-sm text-danger',
  success: 'rounded-xl border border-success-line bg-success-soft px-3.5 py-2.5 text-sm text-success',
  info: 'rounded-xl border border-info-line bg-info-soft px-3.5 py-2.5 text-sm text-info',
  warning: 'rounded-xl border border-warning-line bg-warning-soft px-3.5 py-2.5 text-sm text-warning',
}

/**
 * A state worth noticing: verified, unresolved, over budget.
 *
 * It used to be a filled capsule in 10px uppercase. On a page carrying one of
 * them that reads as emphasis; on a page carrying twenty — the library detail
 * had about twenty-five — every one of them shouts and none of them is heard,
 * and the uppercase tracking made the smallest text on the page the hardest to
 * read. Colour already says which state this is, so the capsule was carrying
 * nothing but weight.
 *
 * What still earns a filled background is a whole message, which is `notice`.
 */
export const tag = 'inline-flex items-center gap-1.5 text-[11px] font-medium'

/** The colour a `tag` carries. Pair with `tagDot` when it needs finding fast. */
export const tagTone = {
  neutral: 'text-muted',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
  danger: 'text-danger',
}

/** A tag's colour as a dot, for a list where the eye has to land on one row. */
export const tagDot = 'h-1.5 w-1.5 shrink-0 rounded-full bg-current'

/** Page header: eyebrow, title, subtitle. */
export const pageTitle = 'text-2xl font-semibold tracking-tight sm:text-[1.75rem]'
export const pageIntro = 'mt-1.5 max-w-xl text-sm text-muted'

export function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ')
}
